import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  IconButton,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import { Fragment, useMemo, useState } from "react";
import { useAppData } from "store/appData";
import { domainBaseUrl, projectDomain } from "utils/domainUrl";
import { regionKey } from "utils/regionKey";
import {
  formatProjectLabel,
  useDatasetViewerSettings,
} from "views/DatasetViewer/hooks/useDatasetViewerSettings";
import MergeButton from "./MergeButton";
import MergeDialog, { type MergeTarget } from "./MergeDialog";
import SinceCell from "./SinceCell";
import StatusBar from "./StatusBar";
import { groupProjectStatuses } from "./groupProjectStatuses";
import type { ProjectLidarUploadStatus } from "./schemas";
import t from "./translations";

const numericCellProps = {
  align: "right",
  sx: { fontVariantNumeric: "tabular-nums" },
} as const;

type Props = {
  projectStatuses: ProjectLidarUploadStatus[];
  onRemoveProject: (projectReference: string) => void;
};

export default function StatusTable({
  projectStatuses,
  onRemoveProject,
}: Props) {
  const [mergeTarget, setMergeTarget] = useState<MergeTarget | null>(null);
  const { domains, projectList } = useAppData();
  const { projectNameDisplay } = useDatasetViewerSettings();
  const lidarUploadUrlByReference = useMemo(() => {
    const urls = new Map<string, string>();
    for (const project of projectList) {
      const domain = projectDomain(project, domains);
      if (domain) {
        urls.set(
          regionKey(project.region, project.id),
          `${domainBaseUrl(domain)}/${project.id}/upload/lidar`
        );
      }
    }
    return urls;
  }, [projectList, domains]);

  const projectStatusGroups = useMemo(
    () => groupProjectStatuses(projectStatuses),
    [projectStatuses]
  );

  return (
    <>
      <TableContainer component={Paper} data-testid="lidar-upload-status.table">
        <Table size="small" sx={{ minWidth: 760 }}>
          <TableHead>
            <TableRow>
              <TableCell>{t.ColumnProject}</TableCell>
              <TableCell align="right">{t.ColumnUploadedFiles}</TableCell>
              <TableCell align="right">{t.ColumnScenes}</TableCell>
              <TableCell align="center" sx={{ width: "45%", minWidth: 360 }}>
                {t.ColumnStatus}
              </TableCell>
              <TableCell>{t.ColumnStateReached}</TableCell>
              <TableCell padding="checkbox" />
            </TableRow>
          </TableHead>
          <TableBody>
            {projectStatusGroups.map((group) => (
              <Fragment key={group.key}>
                <TableRow
                  data-testid={`lidar-upload-status.group.${group.key}`}
                  sx={{ bgcolor: "action.selected" }}
                >
                  <TableCell colSpan={6} sx={{ fontWeight: 700 }}>
                    {group.groupName ?? t.NoGroup}
                    <Box
                      component="span"
                      sx={{ ml: 1, color: "text.secondary", fontWeight: 400 }}
                    >
                      {group.region.toUpperCase()} ·{" "}
                      {t.GroupProjectCount(group.projectStatuses.length)}
                    </Box>
                  </TableCell>
                </TableRow>
                {group.projectStatuses.map((projectStatus) => {
                  const reference = regionKey(
                    projectStatus.region,
                    projectStatus.id
                  );
                  const projectLabel = formatProjectLabel(
                    projectStatus,
                    projectNameDisplay
                  );
                  return (
                    <TableRow
                      key={reference}
                      hover
                      data-testid={`lidar-upload-status.row.${reference}`}
                    >
                      <TableCell>
                        {lidarUploadUrlByReference.has(reference) ? (
                          <Link
                            href={lidarUploadUrlByReference.get(reference)}
                            target="_blank"
                            rel="noopener noreferrer"
                            underline="hover"
                            data-testid={`lidar-upload-status.project-link.${reference}`}
                          >
                            {projectLabel}
                          </Link>
                        ) : (
                          projectLabel
                        )}
                      </TableCell>
                      <TableCell {...numericCellProps}>
                        {projectStatus.uploaded_file_source === "s3" && (
                          <Tooltip title={t.S3SourceTooltip} arrow>
                            <Box
                              component="span"
                              data-testid={`lidar-upload-status.s3-source.${reference}`}
                              sx={{
                                mr: 0.75,
                                color: "text.secondary",
                                fontSize: 11,
                                fontWeight: 700,
                              }}
                            >
                              {t.S3Source}
                            </Box>
                          </Tooltip>
                        )}
                        {projectStatus.uploaded_file_count}
                      </TableCell>
                      <TableCell {...numericCellProps}>
                        {projectStatus.scene_count}
                      </TableCell>
                      <TableCell>
                        <StatusBar
                          projectStatus={projectStatus}
                          reference={reference}
                        />
                      </TableCell>
                      <SinceCell
                        projectStatus={projectStatus}
                        reference={reference}
                      />
                      <TableCell
                        padding="checkbox"
                        sx={{ whiteSpace: "nowrap" }}
                      >
                        <MergeButton
                          hasRawFiles={projectStatus.raw_file_count > 0}
                          reference={reference}
                          onClick={() =>
                            setMergeTarget({
                              region: projectStatus.region,
                              projectId: projectStatus.id,
                              projectLabel,
                            })
                          }
                        />
                        <IconButton
                          size="small"
                          aria-label={t.RemoveProject}
                          onClick={() => onRemoveProject(reference)}
                          data-testid={`lidar-upload-status.remove.${reference}`}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <MergeDialog target={mergeTarget} onClose={() => setMergeTarget(null)} />
    </>
  );
}
